/**
 * iframeBridge.ts
 *
 * A utility module that facilitates communication with a hidden iframe for:
 * 1. Secure cross-domain storage operations
 * 2. Ad-blocker extension detection and interaction
 *
 * This module provides:
 * - Methods to send messages to and receive responses from the iframe
 * - Event listeners for extension detection and status updates
 * - Secure origin validation to prevent unauthorized access
 * - Robust error handling and logging
 *
 * The communication is based on the postMessage API with timeout handling
 * and proper cleanup to prevent memory leaks.
 */

import { getAssetURL } from "../assets";
import { pageURLObject } from "./topWindow";

import type { IframeMessage, IframeResponse } from "../types";

/**
 * URL of the storage iframe HTML file
 */
const STORAGE_URL = getAssetURL("bt-rlink-storage.html");

/**
 * ID to assign to the created iframe element
 */
const IFRAME_ID = "bt-rlink-iframe";

/**
 * Timeout in milliseconds for iframe communication
 * After this period, requests will fail with a timeout error
 */
const IFRAME_TIMEOUT = 5000;

/**
 * Reference to the iframe element once created
 */
let iframe: HTMLIFrameElement | null = null;

/**
 * Promise representing the ongoing iframe initialization
 * Used to prevent multiple concurrent initialization attempts
 */
let initializationPromise: Promise<HTMLIFrameElement> | null = null;

/**
 * Flag indicating whether the global event listener is already set up
 * Prevents adding duplicate event listeners
 */
let isEventListenerSetup = false;

/**
 * Validates whether a message origin is from a trusted source
 * @param origin - The origin URL to validate
 * @returns True if the origin is trusted, false otherwise
 *
 * In production, accepts messages only from:
 * - The current window origin
 * - https://cdn.btmessage.com
 * - https://cdn.btmessage-dev.com
 *
 * In development, accepts messages from:
 * - The current window origin
 * - The storage iframe's origin
 */
const isValidOrigin = (origin: string): boolean => {
  try {
    const currentOrigin = window.location.origin;
    const storageOrigin = new URL(STORAGE_URL).origin;

    if (import.meta.env.DEV) {
      return origin === currentOrigin || origin === storageOrigin;
    }

    return (
      origin === currentOrigin ||
      origin === "https://cdn.btmessage.com" ||
      origin === "https://cdn.btmessage-dev.com"
    );
  } catch {
    return false;
  }
};

/**
 * Creates and initializes the communication iframe
 * @returns Promise that resolves with the iframe element when loaded
 * @throws Error if iframe fails to load
 */
const initializeIframe = (): Promise<HTMLIFrameElement> => {
  return new Promise((resolve, reject) => {
    iframe = document.createElement("iframe");
    iframe.src = STORAGE_URL;
    iframe.id = IFRAME_ID;
    iframe.style.display = "none";
    document.body.appendChild(iframe);

    iframe.onload = () => {
      iframe.contentWindow.postMessage(
        {
          type: "PARENT_INFO",
          domain: pageURLObject.origin,
        },
        iframe.src,
      );
      resolve(iframe as HTMLIFrameElement);
    };
    iframe.onerror = () => {
      iframe = null;
      initializationPromise = null;
      reject(new Error("Failed to load iframe"));
    };
  });
};

/**
 * Sets up event listeners for iframe communication
 * This function is idempotent and will only set up listeners once
 *
 * Adds a global message event listener that:
 * 1. Validates message origins for security
 * 2. Distributes incoming events to registered callbacks based on event type
 * 3. Maintains the isEventListenerSetup flag to prevent duplicate listeners
 *
 * This is called automatically during iframe initialization
 */
const setupEventListener = () => {
  if (isEventListenerSetup) return;

  window.addEventListener("message", (event) => {
    if (!isValidOrigin(event.origin)) {
      return;
    }
  });

  isEventListenerSetup = true;
};

/**
 * Initializes the iframe if it doesn't already exist
 * @returns Promise that resolves to the existing iframe or creates a new one
 * @throws Error if initialization fails
 */
const initIframe = async (): Promise<HTMLIFrameElement> => {
  /**
   * Fast path: return existing iframe when already initialized
   */
  if (iframe) {
    return iframe;
  }

  /**
   * Check if iframe already exists in the DOM
   */
  const existingIframe = document.getElementById(
    IFRAME_ID,
  ) as HTMLIFrameElement;
  if (existingIframe) {
    iframe = existingIframe;
    return iframe;
  }

  /**
   * Return existing promise if initialization is already in progress
   * This prevents multiple concurrent initialization attempts
   */
  if (initializationPromise) {
    return initializationPromise;
  }

  try {
    initializationPromise = initializeIframe();
    iframe = await initializationPromise;
    setupEventListener();
    return iframe;
  } catch (error) {
    iframe = null;
    initializationPromise = null;
    throw error;
  }
};

/**
 * Initializes the RLink communication iframe asynchronously.
 * Ensures the iframe is ready for communication but allows other non-dependent
 * code to run concurrently. Should be awaited before any storage operations.
 *
 * @returns {Promise<void>} A promise that resolves when the iframe is initialized.
 */
export async function initializeRLinkIframe(): Promise<void> {
  // Use the existing initIframe logic which handles idempotency
  await initIframe();
}

/**
 * Sends a message to the iframe and returns the response
 * @template T - The expected response value type
 * @param message - The message to send to the iframe
 * @returns Promise resolving to the iframe's response
 * @throws Error if iframe fails to initialize, message sending fails, or response times out
 */
export const sendMessage = async <T>(message: IframeMessage): Promise<T> => {
  const iframeElement = await initIframe();

  if (!iframeElement.contentWindow) {
    throw new Error("Iframe not initialized properly");
  }

  return new Promise((resolve, reject) => {
    let isResponseReceived = false;
    let timeoutId: number | null = null;

    /**
     * Processes messages received from the iframe
     *
     * Matches messages with the expected source based on message type:
     * - For "storage" messages: expects "bt-rlink-storage" source
     * - For other messages: expects "bt-rlink-extension" source
     *
     * Resolves or rejects the promise based on response status
     */
    const handleResponse = (event: MessageEvent) => {
      if (!isValidOrigin(event.origin)) {
        return;
      }

      const expectedSource =
        message.messageType === "storage"
          ? "bt-rlink-storage"
          : "bt-rlink-extension";

      if (event.data?.source === expectedSource) {
        isResponseReceived = true;
        const response = event.data as IframeResponse<T>;

        if (response.status === "success") {
          cleanup();
          resolve(response.value);
        } else if (response.status === "error") {
          cleanup();
          reject(new Error(response.error || "Request failed"));
        } else {
          cleanup();
          reject(new Error(`Unexpected response status: ${response.status}`));
        }
      }
    };

    /**
     * Removes event listeners and clears timeouts to prevent memory leaks
     * Called after response handling or on error to ensure proper cleanup
     */
    const cleanup = () => {
      window.removeEventListener("message", handleResponse);
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    /**
     * Dynamic timeout value based on operation type
     * - Standard operations: uses default IFRAME_TIMEOUT
     * - Allowlist operations: uses extended timeout (2x) as they involve more complex processing
     */
    const timeout =
      message.action === "allowlist-website"
        ? IFRAME_TIMEOUT * 2
        : IFRAME_TIMEOUT;

    timeoutId = window.setTimeout(() => {
      if (!isResponseReceived) {
        cleanup();
        reject(new Error("Iframe response timeout"));
      }
    }, timeout);

    window.addEventListener("message", handleResponse);

    /**
     * Posts the message to the iframe using window.postMessage API
     * Implements defensive checks to ensure the iframe is valid before posting
     * Properly handles and logs any errors during the postMessage operation
     */
    try {
      if (!iframeElement || !iframeElement.contentWindow) {
        throw new Error("Iframe not initialized properly");
      }
      iframeElement.contentWindow.postMessage(message, "*");
    } catch {
      cleanup();
      reject(new Error("Failed to send message to iframe"));
    }
  });
};
